import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  API_RESPONSE_CODE,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  createStockItemQuery,
  listStockItemsQuery,
  postStockMovementQuery,
  stockLedgerQuery
} from "../../Graphql/querries/stockItemQuery";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import { RootState } from "../../store/store";

type StockItem = {
  id?: string;
  name?: string;
  quantityOnHand?: number;
  baseUnit?: string;
};

const StockItemList: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const storeId = userDetails?.storeId ?? "";

  const [openCreate, setOpenCreate] = useState(false);
  const [name, setName] = useState("");
  const [ledgerItemId, setLedgerItemId] = useState<string | null>(null);
  const [stockInQty, setStockInQty] = useState("10");

  const {
    data: stockItems = [],
    isPending,
    refetch
  } = useQuery({
    queryKey: ["stockItems"],
    queryFn: async () => {
      const response = await fetchGraphQL<{
        listStockItems: {
          code: string;
          stockItems?: StockItem[];
        };
      }>(listStockItemsQuery, { input: { size: 100 } });
      const { code, stockItems } = response.listStockItems;
      return code === API_RESPONSE_CODE.OK ? (stockItems ?? []) : [];
    }
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await fetchGraphQL<{
        createStockItem: { code: string; id?: string };
      }>(createStockItemQuery, {
        input: { name: name.trim(), storeId: storeId || undefined }
      });
      const { code } = response.createStockItem;
      if (code !== API_RESPONSE_CODE.OK) throw new Error(code);
    },
    onSuccess: () => {
      setOpenCreate(false);
      setName("");
      refetch();
    }
  });

  const stockInMutation = useMutation({
    mutationFn: async (stockItemId: string) => {
      const response = await fetchGraphQL<{
        postStockMovement: { code: string };
      }>(postStockMovementQuery, {
        input: {
          stockItemId,
          storeId,
          movementType: "STOCK_IN",
          quantity: Number(stockInQty),
          notes: "Manual stock-in"
        }
      });
      const { code } = response.postStockMovement;
      if (code !== API_RESPONSE_CODE.OK) throw new Error(code);
    },
    onSuccess: () => refetch()
  });

  const { data: ledgerMovements = [] } = useQuery({
    queryKey: ["stockLedger", ledgerItemId],
    enabled: Boolean(ledgerItemId),
    queryFn: async () => {
      const response = await fetchGraphQL<{
        stockLedger: {
          code: string;
          movements?: {
            movementType?: string;
            quantity?: number;
            createdAt?: string;
          }[];
        };
      }>(stockLedgerQuery, { input: { stockItemId: ledgerItemId } });
      const { code, movements } = response.stockLedger;
      return code === API_RESPONSE_CODE.OK ? (movements ?? []) : [];
    }
  });

  const selectedItemName = useMemo(
    () => stockItems.find((item) => item.id === ledgerItemId)?.name,
    [stockItems, ledgerItemId]
  );

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t("loading-stock-items", "Loading stock items...")}
      />
    );
  }

  return (
    <Box data-testid="stock-item-list-page" sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" component="h1">
          {t("stock-items-title", "Stock items")}
        </Typography>
        <Button variant="contained" onClick={() => setOpenCreate(true)}>
          {t("add-stock-item", "Add stock item")}
        </Button>
      </Box>

      {stockItems.length === 0 ? (
        <Typography color="text.secondary">
          {t(
            "stock-items-empty",
            "No stock items yet. Add kitchen supplies or consumables."
          )}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {stockItems.map((item) => (
            <Box
              component="li"
              key={item.id}
              sx={{
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider"
              }}
            >
              <Typography fontWeight={600}>{item.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t("on-hand", "On hand")}: {item.quantityOnHand ?? 0}{" "}
                {item.baseUnit ?? ""}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <LoadingButton
                  size="small"
                  variant="outlined"
                  disabled={!storeId}
                  loading={stockInMutation.isPending}
                  onClick={() => item.id && stockInMutation.mutate(item.id)}
                >
                  {t("stock-in", "Stock in")}
                </LoadingButton>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setLedgerItemId(item.id ?? null)}
                >
                  {t("view-ledger", "Ledger")}
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Dialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("add-stock-item", "Add stock item")}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("name", "Name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          />
          <TextField
            label={t("stock-in-quantity", "Default stock-in qty")}
            value={stockInQty}
            onChange={(e) => setStockInQty(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpenCreate(false)}>
            {t("cancel", "Cancel")}
          </Button>
          <LoadingButton
            variant="contained"
            disabled={!name.trim()}
            loading={createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {t("create", "Create")}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(ledgerItemId)}
        onClose={() => setLedgerItemId(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t("stock-ledger-title", "Stock ledger")}: {selectedItemName}
        </DialogTitle>
        <DialogContent>
          {ledgerMovements.length === 0 ? (
            <Typography color="text.secondary">
              {t("ledger-empty", "No movements yet.")}
            </Typography>
          ) : (
            ledgerMovements.map((m) => (
              <Typography
                key={`${m.createdAt}-${m.quantity}`}
                variant="body2"
                sx={{ py: 0.5 }}
              >
                {m.movementType} · {m.quantity} · {m.createdAt}
              </Typography>
            ))
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default StockItemList;
